"use client";

import { MAX_GUESSES, TILE_FLIP_STAGGER_MS, WORD_LENGTH } from "../_logic/constants";
import type { RoundState, ScoredGuess } from "../_logic/types";
import { STATUS_CLASSES } from "./tileStyles";
import styles from "./wordle.module.css";

interface WordleBoardProps {
  guesses: ScoredGuess[];
  currentGuess: string;
  state: RoundState;
  shakeKey: number;
}

/**
 * The 6×5 grid. Submitted rows flip in letter by letter; the active row pops as you type and shakes on a rejected guess.
 * @param props Guesses so far, the row being typed, round state and a counter that triggers the shake.
 * @returns The board.
 */
export function WordleBoard({ guesses, currentGuess, state, shakeKey }: WordleBoardProps) {
  // Derived values
  const activeRow = state === "playing" ? guesses.length : -1;
  const winningRow = state === "won" ? guesses.length - 1 : -1;

  // Render
  return (
    <div className="flex flex-col gap-1.5" role="grid" aria-label="Wordle board">
      {Array.from({ length: MAX_GUESSES }, (_, row) => {
        const submitted = guesses[row];
        const isActive = row === activeRow;
        const letters = submitted ? submitted.word : isActive ? currentGuess : "";

        return (
          <div
            key={isActive ? `active-${shakeKey}` : row}
            role="row"
            className={`grid grid-cols-5 gap-1.5 ${isActive && shakeKey > 0 ? styles.shake : ""}`}
          >
            {Array.from({ length: WORD_LENGTH }, (_, col) => {
              const letter = letters[col] ?? "";
              const status = submitted?.statuses[col];
              const delay = col * TILE_FLIP_STAGGER_MS;
              const animation = status ? styles.flip : letter ? styles.pop : "";
              const look = status
                ? STATUS_CLASSES[status]
                : letter
                  ? "border-slate-500 bg-white text-slate-900"
                  : "border-slate-200 bg-white text-slate-900";

              return (
                <div
                  key={`${col}-${letter}-${status ?? ""}`}
                  role="gridcell"
                  aria-label={letter ? `${letter}${status ? `, ${status}` : ""}` : "empty"}
                  style={status ? { animationDelay: `${delay}ms` } : undefined}
                  className={`flex aspect-square w-full items-center justify-center rounded-md border-2 text-[clamp(1.5rem,6vw,2rem)] font-bold uppercase select-none ${look} ${animation}`}
                >
                  <span
                    className={row === winningRow ? styles.bounce : ""}
                    style={row === winningRow ? { animationDelay: `${600 + delay}ms` } : undefined}
                  >
                    {letter}
                  </span>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
